import React from 'react';
import { useTranslation } from 'react-i18next';
import { Book } from '@/components/Icons/Book';
import { Calendar } from '@/components/Icons/Calendar';
import { Close } from '@/components/Icons/Close';
import { Door } from '@/components/Icons/Door';
import { LessonVideoChip } from '@/components/LessonVideoChip';
import { LessonDetailsChip } from '@/components/LessonDetailsModal/LessonDetailsChip';
import type { LessonHeading } from '@/types/diary';
import { formatWeekdayDayMonth } from '@/utils/date';

interface Props {
  heading?: LessonHeading;
  hasVideoMeeting: boolean;
  onClose: () => void;
}

export const LessonDetailsHeader: React.FC<Props> = ({
  heading,
  hasVideoMeeting,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <header className="hidden shrink-0 items-center gap-3 p-5 md:flex">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {heading && (
          <>
            <span className="text-subhead-14 flex size-6 shrink-0 items-center justify-center rounded-full bg-on-primary-surface text-foreground">
              {heading.lessonNumber ?? (
                <Book className="size-4 text-subtle-foreground" />
              )}
            </span>
            <h2 className="text-headline-20 min-w-0 truncate text-foreground">
              {heading.title}
            </h2>
            {!!heading.roomNumber && (
              <LessonDetailsChip
                className="ml-1"
                icon={
                  <Door className="size-[15px] shrink-0" strokeWidth="1.2" />
                }
                label={heading.roomNumber}
              />
            )}
            {hasVideoMeeting && <LessonVideoChip className="ml-1" />}
          </>
        )}
      </div>

      {heading && (
        <LessonDetailsChip
          icon={<Calendar className="size-[15px] shrink-0" strokeWidth="1.2" />}
          label={formatWeekdayDayMonth(heading.date)}
        />
      )}

      <button
        type="button"
        aria-label={t('global.close')}
        onClick={onClose}
        className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-foreground"
      >
        <Close className="size-4" />
      </button>
    </header>
  );
};
